import { urlSchema } from '../../../schemas/common/url.js';
import { ctaItemChildSchema, ctaConfigSchema } from '../../CtaConfig.js';
import { parseVideoUrl } from '../../../utils/parseVideoUrl.js';
import { z } from 'zod';
import { serviceConfigSchema } from './SharedServiceConfig.js';

// ============================================================================
// Constants (kept as-is)
// ============================================================================
/**
 * Supported social media platforms
 */
const SOCIAL_MEDIA_PLATFORM_ENUM = {
    FACEBOOK: 'Facebook',
    LINKEDIN: 'LinkedIn',
    INSTAGRAM: 'Instagram',
    X: 'X',
    YOUTUBE: 'YouTube',
    TIKTOK: 'TikTok',
};
const socialMediaPlatformValues = [
    SOCIAL_MEDIA_PLATFORM_ENUM.FACEBOOK,
    SOCIAL_MEDIA_PLATFORM_ENUM.LINKEDIN,
    SOCIAL_MEDIA_PLATFORM_ENUM.INSTAGRAM,
    SOCIAL_MEDIA_PLATFORM_ENUM.X,
    SOCIAL_MEDIA_PLATFORM_ENUM.YOUTUBE,
    SOCIAL_MEDIA_PLATFORM_ENUM.TIKTOK,
];
const SocialMediaPlatformSchema = z.enum(socialMediaPlatformValues);
// ============================================================================
// Carthage Section Schemas
// ============================================================================
// ----------------------------------------
// Footer Configuration Schemas
//
const footerLinkSchema = z.object({
    label: z.string(),
    href: urlSchema,
});
const footerLinkGroupSchema = z.object({
    title: z.string(),
    links: z.array(footerLinkSchema),
});
const socialMediaLinkSchema = z.object({
    platform: SocialMediaPlatformSchema,
    href: urlSchema,
});
const storeFooterConfigSchema = z.object({
    /** Navigation link groups displayed in the main footer area */
    linkGroups: z.array(footerLinkGroupSchema).optional(),
    /** Additional links displayed below the main navigation groups */
    additionalLinks: z.array(footerLinkSchema).optional(),
    /** Social media links */
    socialLinks: z.array(socialMediaLinkSchema).optional(),
});
const topBarItemSchema = ctaItemChildSchema.extend({
    items: z.array(ctaItemChildSchema).optional(),
    parentItemId: z.string().optional(),
});
// ----------------------------------------
// VDP Content Configuration Schemas
//
const cosmeticReportVdpConfigSchema = z.object({
    /** Whether to show the cosmetic report section on the VDP */
    enabled: z.boolean(),
    /** Short summary of the cosmetic report process */
    processSummary: z.string().optional(),
    /** Detailed description of the cosmetic report process */
    processDetails: z.string().optional(),
    /** URL of the process video shown in the cosmetic report section. Must be a YouTube URL. */
    processVideoUrl: urlSchema
        .refine((url) => parseVideoUrl(url)?.vendor === 'youtube', {
        message: 'Only YouTube video URLs are supported',
    })
        .optional(),
});
const googleReviewsVdpConfigSchema = z.object({
    enabled: z.boolean(),
});
// ----------------------------------------
// Core Carthage Section Schema
//
const carthageDomainsConfigSchema = z.object({
    /** List of services providers whose inventory will be shown on their dealer website */
    inventoryServicesProviderIds: z.array(z.string()).optional(),
    /** Sorting key for this services provider's recommended sort algorithm */
    recommendedSortKey: z.custom().optional(),
    /** Store footer configuration */
    storeFooter: storeFooterConfigSchema.optional(),
    /** Store Top Bar configuration */
    storeTopBar: z.array(topBarItemSchema).optional(),
    /** CTA configuration for various placements */
    ctaConfig: ctaConfigSchema.optional(),
    /** Cosmetic report section config for the VDP */
    cosmeticInspection: cosmeticReportVdpConfigSchema.optional(),
    /** Google Reviews section config for the VDP */
    googleReviews: googleReviewsVdpConfigSchema.optional(),
});
// ============================================================================
// Subdomains Section Schemas
// ============================================================================
const subdomainsSchema = z.object({
    client: z.object({
        buyerPortal: urlSchema.optional(), // optional. fall back to below if not present
        checkout: urlSchema.optional(), // optional. fall back to below if not present
        shop: urlSchema.optional(),
    }),
    ekho: z.object({
        buyerPortal: urlSchema,
        checkout: urlSchema,
        shop: urlSchema.optional(),
    }),
});
// ============================================================================
// Checkout Section Schemas
// ============================================================================
const domainsCheckoutConfigSchema = z.object({
    /** Default shipping fee to display when shipping fees have not been calculated yet */
    defaultShippingFee: z.number().optional(),
});
// ============================================================================
// Core Schema
// ============================================================================
const domainsServiceConfigSchema = serviceConfigSchema.extend({
    carthage: carthageDomainsConfigSchema.optional(),
    checkout: domainsCheckoutConfigSchema.optional(),
    subdomains: subdomainsSchema,
});
// ============================================================================
// DTO Schemas
// ============================================================================
const domainsServiceConfigDtoSchema = serviceConfigSchema.extend({
    carthage: carthageDomainsConfigSchema.optional(),
    checkout: domainsCheckoutConfigSchema.optional(),
    subdomains: subdomainsSchema,
});
// DTO for the PATCH /domains-vdp-config endpoint — accepts only the VDP sub-config
const domainsVdpConfigDtoSchema = z.object({
    cosmeticInspection: cosmeticReportVdpConfigSchema.optional(),
    googleReviews: googleReviewsVdpConfigSchema.optional(),
});

export { SOCIAL_MEDIA_PLATFORM_ENUM, SocialMediaPlatformSchema, carthageDomainsConfigSchema, cosmeticReportVdpConfigSchema, domainsCheckoutConfigSchema, domainsServiceConfigDtoSchema, domainsServiceConfigSchema, domainsVdpConfigDtoSchema, footerLinkGroupSchema, footerLinkSchema, googleReviewsVdpConfigSchema, socialMediaLinkSchema, socialMediaPlatformValues, storeFooterConfigSchema, subdomainsSchema, topBarItemSchema };
//# sourceMappingURL=DomainsServiceConfig.js.map
